/**
 * @file DailyReportPage.jsx
 * @description Daily report for ONE IST day (default: yesterday), split by team:
 *              • RENTAL  — every telematics-installed machine (test units excluded):
 *                          ran / not-run / moved / overtime, with two red attention
 *                          topics — On-Rent machines that did NOT run and De-hired
 *                          machines that DID run.
 *              • SERVICE — the day's alarms (faults, thresholds, geofence): what
 *                          came, how severe, and which machines are affected.
 *              The summary icon cards act as FILTERS: clicking a card shows only
 *              that topic's section (not all sections stacked).
 *
 *              EVERY ROW IS A LINK, and the destination follows what the row is
 *              ABOUT (see the nav helpers in the component):
 *                • a CAN error / warning row → the machine's VISUALISATION for the
 *                  report day with the FRAME OF THAT ERROR selected
 *                  (`/mewp-detail/:eq?view=viz&date&at&alarm`) — map position,
 *                  machine state and every CAN signal as they were at that instant.
 *                • a machine-day row (no signal, working fine, ran, did not run,
 *                  moved, overtime) → the same visualisation, opened on the day as
 *                  a whole (`/mewp-detail/:eq?view=viz&date`).
 *                • the serial number itself → the machine's profile page
 *                  (`/rental-machine/:eq`), for spec/contract/operator context.
 */
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, CircularProgress, Alert, Button,
  Tooltip, ToggleButton, ToggleButtonGroup, Link as MuiLink,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as RanIcon,
  RemoveCircle as NotRunIcon,
  SwapHoriz as MovedIcon,
  AccessTimeFilled as OvertimeIcon,
  Print as PrintIcon,
  Place as PlaceIcon,
  LocalShipping as RentalIcon,
  Engineering as ServiceIcon,
  ReportProblem as AlertIcon,
  NotificationsActive as AlarmIcon,
  Error as CriticalIcon,
  Warning as WarningIcon,
  PrecisionManufacturing as MachineIcon,
  EventBusy as DehiredRanIcon,
  CheckCircle as OkIcon,
  SignalCellularOff as OfflineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import reportsService from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';
import { canSeeRentalReport, canSeeServiceReport } from '../../utils/permissions';

// --- date helpers (all YYYY-MM-DD, treated as UTC midnight to avoid TZ drift) ---
const addDays = (ymd, n) => {
  const d = new Date(ymd + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split('T')[0];
};
const prettyDate = (ymd) => {
  if (!ymd) return '';
  return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
};

// Decimal hours → "22h 41m" / "48m" / "3h" (overtime is now span-based, so it
// carries minutes; a plain "22.68 h" reads badly).
const fmtHours = (dec) => {
  const v = Number(dec);
  if (!v || isNaN(v) || v <= 0) return '0h';
  let h = Math.floor(v), m = Math.round((v - h) * 60);
  if (m === 60) { h += 1; m = 0; }
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const getWorkingHours = (m) => {
  if (!m || !m.first_on || !m.last_off || m.first_on === '—' || m.last_off === '—' || m.first_on === '-' || m.last_off === '-') return '0';
  const [h1, m1] = m.first_on.split(':').map(Number);
  const [h2, m2] = m.last_off.split(':').map(Number);
  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return '0';
  const diffMinutes = (h2 * 60 + m2) - (h1 * 60 + m1);
  const hrs = Math.max(0, diffMinutes / 60);
  return hrs.toFixed(1);
};

const STATUS_CHIP = {
  'On Rent':          { bg: '#2563eb15', fg: '#2563eb' },
  'De-hired':         { bg: '#6b728015', fg: '#6b7280' },
  'On Service':       { bg: '#d9770615', fg: '#d97706' },
  'Breakdown':        { bg: '#dc262615', fg: '#dc2626' },
  'Available':        { bg: '#16a34a15', fg: '#16a34a' },
  'Waiting for Rent': { bg: '#7c3aed15', fg: '#7c3aed' },
};

const StatusChip = ({ label }) => {
  const c = STATUS_CHIP[label] || { bg: '#6b728015', fg: '#6b7280' };
  if (!label || label === '—') return null;
  return <Chip size="small" label={label} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, height: 20 }} />;
};

// Serial number is the identifier operators actually use — lead with it.
// equipment_id is an internal handle only and is never shown here; model +
// brand follow underneath so the machine is still fully identified.
//
// The serial is its OWN link: it opens the machine profile, while the row around
// it opens whatever that row is about (the day's data / the CAN frame). Clicking
// the serial must not also fire the row — hence stopPropagation.
const MachineCell = ({ m, onOpenProfile }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
    <Box>
      {onOpenProfile && m.equipment_id ? (
        <Tooltip title="Open machine profile">
          <MuiLink
            component="button"
            type="button"
            underline="hover"
            onClick={(e) => { e.stopPropagation(); onOpenProfile(m.equipment_id); }}
            sx={{
              p: 0, border: 0, background: 'none', cursor: 'pointer', display: 'block',
              textAlign: 'left', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.2,
              color: 'text.primary', fontFamily: 'inherit',
            }}
          >
            {m.serial_number || m.name || '—'}
          </MuiLink>
        </Tooltip>
      ) : (
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {m.serial_number || m.name || '—'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {[m.name, m.brand].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
    <StatusChip label={m.rental_status} />
  </Box>
);

const SummaryCard = ({ icon, label, value, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 2, borderRadius: 3, cursor: onClick ? 'pointer' : 'default', height: '100%',
      border: '1px solid', borderColor: active ? color : 'divider',
      boxShadow: active ? `0 0 0 2px ${color}33` : 'none',
      bgcolor: active ? `${color}08` : 'background.paper',
      transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 1.5,
      '&:hover': onClick ? { borderColor: color, transform: 'translateY(-1px)' } : {},
    }}
  >
    <Box sx={{
      width: 42, height: 42, borderRadius: 2, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${color}18`, color,
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
    </Box>
  </Paper>
);

const Section = ({ title, color, icon, count, children }) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${color}10`, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
      <Chip label={count} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700, height: 22 }} />
    </Box>
    {children}
  </Paper>
);

const Empty = ({ text }) => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">{text}</Typography>
  </Box>
);

// One horizontal row of summary cards — NEVER wraps to a second line; when there
// are more cards than fit, the row scrolls left↔right.
/**
 * The topic strip: `pinned` (date navigator + print) is frozen at the left edge
 * INSIDE the scroller — position:sticky with an opaque background — so the topic
 * cards slide underneath it as you scroll right, instead of pushing it away.
 */
const CardsRow = ({ pinned, children }) => (
  <Box sx={{
    display: 'flex', gap: 2, overflowX: 'auto', pb: 1, mb: 3,
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
  }}>
    {pinned && (
      <Box sx={{
        position: 'sticky', left: 0, zIndex: 2, flex: '0 0 auto',
        display: 'flex', alignItems: 'center', gap: 1,
        // opaque + soft right edge so cards visibly disappear behind it
        bgcolor: 'background.default', pr: 1.5,
        boxShadow: '10px 0 10px -8px rgba(15,23,42,0.16)',
      }}>
        {pinned}
      </Box>
    )}
    {React.Children.map(children, (c) => (c ? <Box sx={{ minWidth: 205, flex: '0 0 auto' }}>{c}</Box> : null))}
  </Box>
);

const COLORS = {
  ran: '#16a34a',
  notRun: '#dc2626',
  moved: '#d97706',
  overtime: '#7c3aed',
  attention: '#dc2626',
  tracked: '#2563eb',
  critical: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
  machines: '#0f766e',
};

const SEVERITY_CHIP = {
  critical: { bg: '#dc2626', fg: '#fff' },
  warning:  { bg: '#d97706', fg: '#fff' },
  info:     { bg: '#2563eb', fg: '#fff' },
};

// Device-reported (MQTT-native alert/DTC) rows are visually distinct from
// computed threshold/geofence alarms — a violet accent + a "DEVICE REPORTED"
// tag, so operators can tell what the machine itself said vs what we computed.
const isDeviceReported = (a) => a.category === 'DEVICE_ALERT' || a.category === 'DEVICE_DTC';
const CATEGORY_LABEL = { CONTRACT_GEOFENCE: 'Geofence', MACHINE: 'Machine', DEVICE_ALERT: 'Device Alert', DEVICE_DTC: 'Device DTC' };
// Theft alert: the machine reported its power was cut — emergency red.
const isPowerLoss = (a) => /power[\s_-]*(off|disconnect|cut|lost|fail|down)/i.test(`${a.alarm_name || ''} ${a.message || ''}`);

const AlarmRow = ({ a, onAlarmClick, onOpenProfile }) => {
  const deviceReported = isDeviceReported(a);
  const powerLoss = isPowerLoss(a);
  return (
  <TableRow
    hover
    sx={{
      cursor: onAlarmClick ? 'pointer' : 'default',
      ...(powerLoss ? { borderLeft: '4px solid #dc2626', bgcolor: 'rgba(220,38,38,0.07)' }
                    : deviceReported ? { borderLeft: '3px solid #7c3aed', bgcolor: 'rgba(124,58,237,0.04)' }
                    : a.severity === 'critical' ? { bgcolor: '#dc262608' } : {})
    }}
    onClick={() => onAlarmClick && onAlarmClick(a)}
  >
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={onAlarmClick ? 'Open the CAN frame received at this time' : ''}>
        <b>{a.time_ist || '—'}</b>
      </Tooltip>
    </TableCell>
    <TableCell>
      {onOpenProfile && a.equipment_id ? (
        <Tooltip title="Open machine profile">
          <MuiLink
            component="button"
            type="button"
            underline="hover"
            onClick={(e) => { e.stopPropagation(); onOpenProfile(a.equipment_id); }}
            sx={{
              p: 0, border: 0, background: 'none', cursor: 'pointer', display: 'block',
              textAlign: 'left', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.2,
              color: 'text.primary', fontFamily: 'inherit',
            }}
          >
            {a.serial_number || a.machine_name || '—'}
          </MuiLink>
        </Tooltip>
      ) : (
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {a.serial_number || a.machine_name || '—'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {[a.machine_name, a.brand].filter(Boolean).join(' · ')}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2" fontWeight={powerLoss ? 900 : 600} sx={{ color: powerLoss ? '#b91c1c' : 'inherit' }}>
        {powerLoss ? 'Power Disconnected' : a.alarm_name}
      </Typography>
      {a.message && <Typography variant="caption" color="text.secondary">{a.message}</Typography>}
    </TableCell>
    <TableCell align="center">
      {powerLoss ? (
        <Chip size="small" label="POSSIBLE THEFT"
          sx={{ height: 20, fontWeight: 900, bgcolor: '#dc2626', color: '#fff' }} />
      ) : deviceReported ? (
        <Chip size="small" label={`DEVICE REPORTED · ${CATEGORY_LABEL[a.category]}`}
          sx={{ height: 20, fontWeight: 800, bgcolor: '#7c3aed', color: '#fff' }} />
      ) : (
        <Chip size="small" label={CATEGORY_LABEL[a.category] || a.category || '—'}
          sx={{ height: 20, fontWeight: 700 }} />
      )}
    </TableCell>
    <TableCell align="center">
      <Chip size="small" label={a.severity}
        sx={{ height: 20, fontWeight: 700, textTransform: 'uppercase',
              bgcolor: (SEVERITY_CHIP[a.severity] || SEVERITY_CHIP.info).bg,
              color: (SEVERITY_CHIP[a.severity] || SEVERITY_CHIP.info).fg }} />
    </TableCell>
    <TableCell align="center">
      <Chip size="small" label={a.alarm_status}
        sx={{ height: 20, fontWeight: 700,
              bgcolor: a.alarm_status === 'OPEN' ? '#dc262615' : '#16a34a15',
              color: a.alarm_status === 'OPEN' ? '#dc2626' : '#16a34a' }} />
    </TableCell>
  </TableRow>
  );
};

const AlarmTable = ({ rows, onAlarmClick, onOpenProfile }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Time (IST)</TableCell>
          <TableCell>Machine</TableCell>
          <TableCell>Alarm</TableCell>
          <TableCell align="center">Category</TableCell>
          <TableCell align="center">Severity</TableCell>
          <TableCell align="center">Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((a) => <AlarmRow key={a.id} a={a} onAlarmClick={onAlarmClick} onOpenProfile={onOpenProfile} />)}
      </TableBody>
    </Table>
  </TableContainer>
);

// Device hardware-status table (Working fine / No signal · offline). Serial-led
// like every other machine list; the danger tone tints offline rows red.
const DeviceStatusTable = ({ rows, tone, onMachineClick, onOpenProfile }) => {
  const danger = tone === 'danger';
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Machine</TableCell>
            <TableCell align="right">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((m) => (
            <TableRow
              key={m.equipment_id}
              hover
              sx={{ cursor: onMachineClick ? 'pointer' : 'default', ...(danger ? { bgcolor: '#dc262608' } : {}) }}
              onClick={() => onMachineClick && onMachineClick(m.equipment_id)}
            >
              <TableCell><MachineCell m={m} onOpenProfile={onOpenProfile} /></TableCell>
              <TableCell align="right">
                <Chip size="small" label={danger ? 'No signal · offline' : 'Working fine'}
                  sx={{ bgcolor: danger ? '#dc2626' : '#16a34a', color: '#fff', fontWeight: 700, height: 22 }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Machine run-table used by several rental topics.
const RunTable = ({ rows, red, onMachineClick, onOpenProfile }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Machine</TableCell>
          <TableCell align="right">Working hrs</TableCell>
          <TableCell align="center">First on</TableCell>
          <TableCell align="center">Last on</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((m) => (
          <TableRow 
            key={m.equipment_id} 
            hover 
            sx={{ 
              cursor: onMachineClick ? 'pointer' : 'default',
              ...(red ? { bgcolor: '#dc262608' } : {}) 
            }}
            onClick={() => onMachineClick && onMachineClick(m.equipment_id)}
          >
            <TableCell><MachineCell m={m} onOpenProfile={onOpenProfile} /></TableCell>
            <TableCell align="right"><b>{getWorkingHours(m)}</b></TableCell>
            <TableCell align="center">{m.first_on || '—'}</TableCell>
            <TableCell align="center">{m.last_off || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * The invoice downloader shown at the bottom of the Log Sheets page is the
 * SAME component the Rentals page uses — RentalPage locked to its
 * invoiceDownload view — so the two can never drift apart.
 *
 * It is loaded lazily on purpose: RentalPage imports this file (for its own
 * Rental Daily Report view), and a static import back would be a cycle.
 * React.lazy defers the import to render time, which breaks it. There is no
 * infinite nesting either — the downloader is rendered only when this page is
 * NOT embedded (i.e. the standalone /reports/daily Log Sheets page), and the
 * embedded RentalPage is pinned to invoiceDownload, never to dailyReport.
 */
const RentalPageLazy = React.lazy(() => import('./RentalPage'));

export default function DailyReportPage({ embedded = false, forcedTeam = null, hideInvoices = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Team visibility follows the user's accessables: rentals key → Rental view,
  // service key → Service view. Super admin (and both-key users) get both.
  // When embedded in a team page (Rentals / Service) the team is FORCED and
  // the toggle is hidden — the page context already says which team it is.
  const canRental = canSeeRentalReport(user);
  const canService = canSeeServiceReport(user);
  const [team, setTeam] = useState(forcedTeam || (canRental ? 'rental' : 'service')); // 'rental' | 'service'
  const [topic, setTopic] = useState('all');      // rental topic filter
  const [svcTopic, setSvcTopic] = useState('all'); // service topic filter
  const [date, setDate] = useState(null);          // resolved after first load
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // If permissions change (relogin) snap the team back into the allowed set.
  useEffect(() => {
    if (forcedTeam) { if (team !== forcedTeam) setTeam(forcedTeam); return; }
    if (team === 'rental' && !canRental && canService) setTeam('service');
    if (team === 'service' && !canService && canRental) setTeam('rental');
  }, [team, canRental, canService, forcedTeam]);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getDaily(d);
      setData(res);
      setDate(res.date);
    } catch (e) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]); // first load → server picks yesterday

  const maxDate = data ? addDays(data.todayIST, -1) : null; // can't view today/future
  const canPrev = !!date;
  const canNext = date && maxDate && date < maxDate;

  const go = (delta) => {
    if (!date) return;
    const next = addDays(date, delta);
    if (maxDate && next > maxDate) return;
    load(next);
  };

  /* ---------------------------------------------------------------------------
   * Row navigation — every row leads to the page that ANSWERS it.
   *
   * Both data destinations are the machine's VISUALISATION for the report day
   * (`/mewp-detail/:eq?view=viz&date=…`) — map trail, activity log, and the
   * frame-by-frame Data Log. They differ only in where they land you:
   *   openMachineDay a "no signal" / "ran" / "did not run" row is about a whole
   *                  DAY, so the page opens on the day as a whole; when nothing
   *                  arrived it says the device was silent instead of drawing
   *                  empty panels.
   *   openCanFrame   a CAN error / warning happened at an INSTANT, so `at` also
   *                  selects the frame the machine sent at that moment — the map,
   *                  the activity log and every signal in the Data Log then read
   *                  the machine's actual state when the error fired.
   *   openProfile    the serial number is identity, so it opens the machine's
   *                  profile (spec sheet, contract, operators).
   *
   * /mewp-detail rather than /devices/:id — the same page, but behind the
   * `machinedetail` key (one machine opened from a list) instead of
   * `devicemanagement` (the device list itself), so a Service- or
   * Operations-only user is not bounced by the route guard.
   * ------------------------------------------------------------------------- */
  const markReturn = () => sessionStorage.setItem('rentalsReturnView', 'dailyReport');

  const openMachineDay = (equipmentId, extra = {}) => {
    if (!equipmentId) return;
    markReturn();
    const q = new URLSearchParams({ view: 'viz', from: 'daily-report', ...extra });
    if (date) q.set('date', date);
    navigate(`/mewp-detail/${encodeURIComponent(equipmentId)}?${q.toString()}`);
  };

  const openCanFrame = (a) => {
    if (!a || !a.equipment_id) return;
    const at = a.created_at ? new Date(a.created_at) : null;
    const extra = {};
    // `at` selects the frame; `alarm` names it in the banner, so the page can say
    // WHICH error you came in on rather than just jumping to a time.
    if (at && !isNaN(at.getTime())) extra.at = at.toISOString();
    if (a.alarm_name) extra.alarm = a.alarm_name;
    openMachineDay(a.equipment_id, extra);
  };

  // The per-machine alarm rollup ("machines affected") names no single instant —
  // the errors panel on the visualisation page lists every alarm of the day.
  const openMachineCanDay = (equipmentId) => openMachineDay(equipmentId);

  const openProfile = (equipmentId) => {
    if (!equipmentId) return;
    markReturn();
    navigate(`/rental-machine/${encodeURIComponent(equipmentId)}`);
  };

  const s = data?.summary || {};
  const alarms = data?.alarms || [];
  const critAlarms = alarms.filter(a => a.severity === 'critical');
  const warnAlarms = alarms.filter(a => a.severity === 'warning');
  const openAlarms = alarms.filter(a => a.alarm_status === 'OPEN');
  // Device hardware status for the day (test devices already excluded server-side).
  const devWorking = data?.device_status?.working || [];
  const devOffline = data?.device_status?.offline || [];

  // All tracked machines = ran + not-run, one row each with a Ran? chip.
  const allMachines = [
    ...(data?.ran || []).map(m => ({ ...m, _ran: true })),
    ...(data?.not_run || []).map(m => ({ ...m, _ran: false })),
  ].sort((a, b) => String(a.equipment_id).localeCompare(String(b.equipment_id)));

  // Per-machine alarm rollup for the "machines affected" topic.
  const byMachine = {};
  for (const a of alarms) {
    const k = a.equipment_id || '—';
    if (!byMachine[k]) byMachine[k] = { equipment_id: a.equipment_id, machine_name: a.machine_name,
      serial_number: a.serial_number, rental_status: a.rental_status, total: 0, critical: 0, open: 0, names: new Set() };
    byMachine[k].total++;
    if (a.severity === 'critical') byMachine[k].critical++;
    if (a.alarm_status === 'OPEN') byMachine[k].open++;
    byMachine[k].names.add(a.alarm_name);
  }
  const machineRollup = Object.values(byMachine).sort((a, b) => b.total - a.total);

  /* Frozen head of the topic strip: the day being reported + Print. It sits where
     the first card used to, and the cards scroll away behind it. */
  const stripControls = (
    <>
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <IconButton size="small" onClick={() => go(-1)} disabled={!canPrev || loading} aria-label="Previous day">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Box sx={{ textAlign: 'center', minWidth: 132 }}>
          <input
            type="date"
            value={date || ''}
            max={maxDate || undefined}
            onChange={(e) => e.target.value && load(e.target.value)}
            aria-label="Report date"
            style={{
              border: 'none', background: 'transparent', fontWeight: 700,
              fontSize: '0.875rem', textAlign: 'center', cursor: 'pointer', color: 'inherit',
              width: '100%', padding: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1, fontSize: 10.5 }}>
            {prettyDate(date)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => go(1)} disabled={!canNext || loading} aria-label="Next day">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Paper>
      <Tooltip title="Print this report">
        <span>
          <IconButton onClick={() => window.print()} disabled={loading} aria-label="Print report"
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper', width: 40, height: 40 }}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {/* Team selector — only for a user who can see both teams. The date navigator
          and Print live in the topic strip below, pinned while the cards scroll. */}
      {!forcedTeam && canRental && canService && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={team}
            exclusive
            onChange={(e, v) => v && setTeam(v)}
            sx={{
              '& .MuiToggleButton-root': {
                px: 2.5, py: 1, textTransform: 'none', fontWeight: 800, gap: 1, borderRadius: 3,
              },
            }}
          >
            <ToggleButton value="rental"><RentalIcon fontSize="small" /> Rental</ToggleButton>
            <ToggleButton value="service"><ServiceIcon fontSize="small" /> Service</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      ) : team === 'rental' ? (
        <>
          {/* RENTAL — icon cards are FILTERS: click one to open just that topic */}
          <CardsRow pinned={stripControls}>
            <SummaryCard icon={<MachineIcon />} label="Tracked machines" value={s.total ?? 0} color={COLORS.tracked} active={topic === 'all'} onClick={() => setTopic('all')} />
            <SummaryCard icon={<AlertIcon />} label="On Rent · did NOT run" value={s.onrent_not_run ?? 0} color={COLORS.attention} active={topic === 'onrent'} onClick={() => setTopic('onrent')} />
            <SummaryCard icon={<DehiredRanIcon />} label="De-hired · ran" value={s.dehired_ran ?? 0} color={COLORS.attention} active={topic === 'dehired'} onClick={() => setTopic('dehired')} />
            <SummaryCard icon={<RanIcon />} label="Ran" value={s.ran ?? 0} color={COLORS.ran} active={topic === 'ran'} onClick={() => setTopic('ran')} />
            <SummaryCard icon={<NotRunIcon />} label="Did not run" value={s.not_run ?? 0} color={COLORS.notRun} active={topic === 'notrun'} onClick={() => setTopic('notrun')} />
            <SummaryCard icon={<MovedIcon />} label="Moved site" value={s.moved ?? 0} color={COLORS.moved} active={topic === 'moved'} onClick={() => setTopic('moved')} />
            <SummaryCard icon={<OvertimeIcon />} label="Overtime" value={s.overtime ?? 0} color={COLORS.overtime} active={topic === 'overtime'} onClick={() => setTopic('overtime')} />
          </CardsRow>

          {/* Only the selected topic renders */}
          {topic === 'all' && (
            <Section title="All tracked machines" color={COLORS.tracked} icon={<MachineIcon />} count={allMachines.length}>
              {allMachines.length === 0 ? <Empty text="No telematics-installed machines found." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Machine</TableCell>
                        <TableCell align="center">Ran?</TableCell>
                        <TableCell align="right">Working hrs</TableCell>
                        <TableCell align="center">First on</TableCell>
                        <TableCell align="center">Last on</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allMachines.map((m) => {
                        const attention = (m.on_rent && !m._ran) || (m.dehired && m._ran);
                        return (
                          <TableRow 
                            key={m.equipment_id} 
                            hover 
                            sx={{ 
                              cursor: 'pointer',
                              ...(attention ? { bgcolor: '#dc262608' } : {}) 
                            }}
                            onClick={() => openMachineDay(m.equipment_id)}
                          >
                            <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={m._ran ? 'Ran' : 'Did not run'}
                                sx={{ height: 20, fontWeight: 700,
                                      bgcolor: attention ? '#dc2626' : (m._ran ? '#16a34a15' : '#6b728015'),
                                      color: attention ? '#fff' : (m._ran ? '#16a34a' : '#6b7280') }} />
                            </TableCell>
                            <TableCell align="right"><b>{m._ran ? getWorkingHours(m) : '—'}</b></TableCell>
                            <TableCell align="center">{m._ran ? (m.first_on || '—') : '—'}</TableCell>
                            <TableCell align="center">{m._ran ? (m.last_off || '—') : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {topic === 'onrent' && (
            <Section title="On Rent — did NOT run" color={COLORS.attention} icon={<AlertIcon />} count={s.onrent_not_run ?? 0}>
              {(data?.onrent_not_run || []).length === 0 ? <Empty text="Every On-Rent machine ran." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>Machine</TableCell><TableCell align="right">Status</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.onrent_not_run || []).map((m) => (
                        <TableRow 
                          key={m.equipment_id} 
                          hover 
                          sx={{ cursor: 'pointer', bgcolor: '#dc262608' }}
                          onClick={() => openMachineDay(m.equipment_id)}
                        >
                          <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                          <TableCell align="right">
                            <Chip size="small" label="On Rent but idle all day" sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {topic === 'dehired' && (
            <Section title="De-hired — ran" color={COLORS.attention} icon={<DehiredRanIcon />} count={s.dehired_ran ?? 0}>
              {(data?.dehired_ran || []).length === 0 ? <Empty text="No de-hired machine ran." /> : (
                <RunTable rows={data?.dehired_ran || []} red onMachineClick={openMachineDay} onOpenProfile={openProfile} />
              )}
            </Section>
          )}

          {topic === 'ran' && (
            <Section title="Machines that ran" color={COLORS.ran} icon={<RanIcon />} count={s.ran ?? 0}>
              {(data?.ran || []).length === 0 ? <Empty text="No machine ran on this day." /> : <RunTable rows={data?.ran || []} onMachineClick={openMachineDay} onOpenProfile={openProfile} />}
            </Section>
          )}

          {topic === 'notrun' && (
            <Section title="Machines that did NOT run" color={COLORS.notRun} icon={<NotRunIcon />} count={s.not_run ?? 0}>
              {(data?.not_run || []).length === 0 ? <Empty text="Every tracked machine ran." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>Machine</TableCell><TableCell align="right">Status</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.not_run || []).map((m) => (
                        <TableRow 
                          key={m.equipment_id} 
                          hover 
                          sx={{ 
                            cursor: 'pointer',
                            ...(m.on_rent ? { bgcolor: '#dc262608' } : {}) 
                          }}
                          onClick={() => openMachineDay(m.equipment_id)}
                        >
                          <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={m.on_rent ? 'On Rent but idle' : 'Idle — no ignition'}
                              sx={{ bgcolor: m.on_rent ? '#dc2626' : `${COLORS.notRun}15`,
                                    color: m.on_rent ? '#fff' : COLORS.notRun, fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {topic === 'moved' && (
            <Section title="Machines that moved site" color={COLORS.moved} icon={<MovedIcon />} count={s.moved ?? 0}>
              {(data?.moved || []).length === 0 ? <Empty text="No machine changed location." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Machine</TableCell>
                        <TableCell align="right">Distance moved</TableCell>
                        <TableCell align="center">Ended at</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.moved || []).map((m) => (
                        <TableRow 
                          key={m.equipment_id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openMachineDay(m.equipment_id)}
                        >
                          <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                          <TableCell align="right">
                            <b>{m.moved_m >= 1000 ? `${(m.moved_m / 1000).toFixed(1)} km` : `${m.moved_m} m`}</b>
                          </TableCell>
                          <TableCell align="center">
                            {m.map_url ? (
                              <Button 
                                size="small" 
                                href={m.map_url} 
                                target="_blank" 
                                rel="noopener" 
                                startIcon={<PlaceIcon />} 
                                sx={{ textTransform: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Map
                              </Button>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {topic === 'overtime' && (
            <Section title="Machines that ran overtime" color={COLORS.overtime} icon={<OvertimeIcon />} count={s.overtime ?? 0}>
              {(data?.overtime || []).length === 0 ? <Empty text="No overtime recorded." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Machine</TableCell>
                        <TableCell align="right">Overtime</TableCell>
                        <TableCell align="center">First on</TableCell>
                        <TableCell align="center">Last on</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.overtime || []).map((m) => (
                        <TableRow 
                          key={m.equipment_id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openMachineDay(m.equipment_id)}
                        >
                          <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={fmtHours(m.overtime_hours)} sx={{ bgcolor: COLORS.overtime, color: '#fff', fontWeight: 700 }} />
                          </TableCell>
                          <TableCell align="center">{m.first_on}</TableCell>
                          <TableCell align="center">{m.last_off}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}
        </>
      ) : (
        <>
          {/* SERVICE — icon cards are FILTERS: click one to open just that topic.
              Alarms here are what the MACHINE reports over CAN (DTC faults +
              device alerts), NOT server-computed threshold alarms. */}
          <CardsRow pinned={stripControls}>
            <SummaryCard icon={<AlarmIcon />} label="CAN alerts" value={s.alarms ?? 0} color={COLORS.tracked} active={svcTopic === 'all'} onClick={() => setSvcTopic('all')} />
            <SummaryCard icon={<CriticalIcon />} label="Faults (errors)" value={s.alarms_critical ?? 0} color={COLORS.critical} active={svcTopic === 'critical'} onClick={() => setSvcTopic('critical')} />
            <SummaryCard icon={<WarningIcon />} label="Warnings" value={s.alarms_warning ?? 0} color={COLORS.warning} active={svcTopic === 'warning'} onClick={() => setSvcTopic('warning')} />
            <SummaryCard icon={<MachineIcon />} label="Machines affected" value={s.alarm_machines ?? 0} color={COLORS.machines} active={svcTopic === 'machines'} onClick={() => setSvcTopic('machines')} />
            <SummaryCard icon={<OkIcon />} label="Working fine" value={s.dev_working ?? 0} color={COLORS.ran} active={svcTopic === 'working'} onClick={() => setSvcTopic('working')} />
            <SummaryCard icon={<OfflineIcon />} label="No signal · offline" value={s.dev_offline ?? 0} color={COLORS.notRun} active={svcTopic === 'offline'} onClick={() => setSvcTopic('offline')} />
            <SummaryCard icon={<NotRunIcon />} label="Did not run" value={s.not_run ?? 0} color={COLORS.notRun} active={svcTopic === 'notrun'} onClick={() => setSvcTopic('notrun')} />
          </CardsRow>

          {svcTopic === 'notrun' && (
            <Section title="Machines that did NOT run" color={COLORS.notRun} icon={<NotRunIcon />} count={s.not_run ?? 0}>
              {(data?.not_run || []).length === 0 ? <Empty text="Every tracked machine ran." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>Machine</TableCell><TableCell align="right">Status</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.not_run || []).map((m) => (
                        <TableRow 
                          key={m.equipment_id} 
                          hover 
                          sx={{ 
                            cursor: 'pointer',
                            ...(m.on_rent ? { bgcolor: '#dc262608' } : {}) 
                          }}
                          onClick={() => openMachineDay(m.equipment_id)}
                        >
                          <TableCell><MachineCell m={m} onOpenProfile={openProfile} /></TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={m.on_rent ? 'On Rent but idle' : 'Idle — no ignition'}
                              sx={{ bgcolor: m.on_rent ? '#dc2626' : `${COLORS.notRun}15`,
                                    color: m.on_rent ? '#fff' : COLORS.notRun, fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {svcTopic === 'all' && (
            <Section title="Machine errors & warnings (CAN)" color={COLORS.tracked} icon={<AlarmIcon />} count={alarms.length}>
              {alarms.length === 0 ? <Empty text="No machine reported a CAN error or warning on this day." /> : <AlarmTable rows={alarms} onAlarmClick={openCanFrame} onOpenProfile={openProfile} />}
            </Section>
          )}

          {svcTopic === 'critical' && (
            <Section title="CAN faults (errors)" color={COLORS.critical} icon={<CriticalIcon />} count={critAlarms.length}>
              {critAlarms.length === 0 ? <Empty text="No CAN fault codes on this day." /> : <AlarmTable rows={critAlarms} onAlarmClick={openCanFrame} onOpenProfile={openProfile} />}
            </Section>
          )}

          {svcTopic === 'warning' && (
            <Section title="CAN warnings" color={COLORS.warning} icon={<WarningIcon />} count={warnAlarms.length}>
              {warnAlarms.length === 0 ? <Empty text="No CAN warnings on this day." /> : <AlarmTable rows={warnAlarms} onAlarmClick={openCanFrame} onOpenProfile={openProfile} />}
            </Section>
          )}

          {svcTopic === 'working' && (
            <Section title="Working fine — CAN & engine ran" color={COLORS.ran} icon={<OkIcon />} count={devWorking.length}>
              {devWorking.length === 0 ? <Empty text="No machine ran with healthy CAN on this day." /> : <DeviceStatusTable rows={devWorking} tone="ok" onMachineClick={openMachineDay} onOpenProfile={openProfile} />}
            </Section>
          )}

          {svcTopic === 'offline' && (
            <Section title="No signal · offline" color={COLORS.notRun} icon={<OfflineIcon />} count={devOffline.length}>
              {devOffline.length === 0 ? <Empty text="Every device reported at least once." /> : <DeviceStatusTable rows={devOffline} tone="danger" onMachineClick={openMachineDay} onOpenProfile={openProfile} />}
            </Section>
          )}

          {svcTopic === 'machines' && (
            <Section title="Machines affected" color={COLORS.machines} icon={<MachineIcon />} count={machineRollup.length}>
              {machineRollup.length === 0 ? <Empty text="No machine raised an alarm on this day." /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Machine</TableCell>
                        <TableCell>Alarms raised</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Critical</TableCell>
                        <TableCell align="right">Still open</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {machineRollup.map((m) => (
                        <TableRow 
                          key={m.equipment_id || 'unknown'} 
                          hover 
                          sx={{ 
                            cursor: 'pointer',
                            ...(m.critical > 0 ? { bgcolor: '#dc262608' } : {}) 
                          }}
                          onClick={() => openMachineCanDay(m.equipment_id)}
                        >
                          <TableCell>
                            <MachineCell m={{ ...m, name: m.machine_name }} onOpenProfile={openProfile} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{[...m.names].slice(0, 4).join(', ')}{m.names.size > 4 ? '…' : ''}</Typography>
                          </TableCell>
                          <TableCell align="right"><b>{m.total}</b></TableCell>
                          <TableCell align="right">{m.critical > 0 ? <Chip size="small" label={m.critical} sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 700, height: 20 }} /> : '0'}</TableCell>
                          <TableCell align="right">{m.open}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}
        </>
      )}

      {/* Invoice downloader — the Rentals page's own one, unchanged. Only on
          the standalone legacy Log Sheets page; the embedded daily reports
          inside Rentals / Service — and the Operations Daily Report page,
          which passes hideInvoices (Operations has its own Log Sheets header
          for invoices) — keep showing just the report. */}
      {!embedded && !hideInvoices && (
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Invoice download</Typography>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
            <RentalPageLazy embedded forcedView="invoiceDownload" />
          </Suspense>
        </Box>
      )}
    </Box>
  );
}
